// deps
import React, { memo, ReactElement, useCallback, useContext, useRef, useState } from "react";

// internal-deps
import {
    DeleteCardPrompt as DeleteCardPromptUI,
    DeleteCardConfirmPrompt as DeleteCardConfirmPromptUI,
    PaymentProgressLoader as Loader,
} from "@swiggy-private/payment-ui";
import {
    ApiContext,
    recordNREvent,
    NEWRELIC_TABLES,
    WP_JUSPAY_CARD_FLOW_ERROR_STATE,
} from "@swiggy-private/checkout-core";
import { useEffectOnce } from "@swiggy-private/core-ui";

//Analytics
import { AnalyticsContext, closeWebview } from "@swiggy-private/checkout-core";
import {
    onDeleteCardCancelEvent,
    onDeleteCardDismissEvent,
    deleteCardSuccessScreenViewEvent,
    deleteHalfCardScreenViewEvent,
} from "@payment-client/analytics/events/DeleteCard";

// constants
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";

// utils
import { showNativeAlert } from "@payment-client/utils/Native";
import { CardDetails } from "@swiggy-private/payment-types";
import { deleteCardPublic } from "@payment-client/apis/publicCard";

const TEXTS = {
    DELETE_CARD_TITLE: "Are you sure you want to delete this card?",
    DELETE_CARD_MESSAGE:
        "Card details will no longer be securely saved. You’ll have to enter card details again to pay with this card.",
    DELETE_CARD_BUTTON_PRIMARY_CTA: "Yes, delete card",
    DELETE_CARD_BUTTON_SECONDARY_CTA: "Cancel",
    DELETE_CARD_CONFIRMATION_TITLE: "Your {{cardBrand}} card was deleted!",
    DELETE_CARD_CONFIRM_BUTTON: "Dismiss",
};

export enum DELETE_CARD_PROMPT_TYPE {
    ALERT = "alert",
    CONFIRM = "confirm",
}

export interface DeleteCardProps {
    cardDetails: CardDetails;
    resetDeleteCardAlert: VoidFunction;
}

const TICK_ICON = "PaymentLogos/paymentIcons/tick";

export const DeleteCardConfirmPrompt = memo(function DeleteCardConfirmPrompt(props: {
    onClose: VoidFunction;
    cardBrand: string;
}): React.ReactElement {
    const { screenViewEvent } = useContext(AnalyticsContext);
    const PRIMARY_ACTION = { text: TEXTS.DELETE_CARD_CONFIRM_BUTTON, onClick: props.onClose };

    useEffectOnce(() => {
        deleteCardSuccessScreenViewEvent(screenViewEvent);
    });

    return (
        <DeleteCardConfirmPromptUI
            title={TEXTS.DELETE_CARD_CONFIRMATION_TITLE}
            message=""
            iconUrl={TICK_ICON}
            primaryAction={PRIMARY_ACTION}
            hideOnOverlayClick={false}
            onClose={props.onClose}
            cardBrand={props.cardBrand}
        />
    );
});

export const DeleteCardPrompt = memo(function DeleteCardAlert(props: {
    onClose: VoidFunction;
    onDeleteCardClick: VoidFunction;
}): React.ReactElement {
    const { screenViewEvent } = useContext(AnalyticsContext);
    const PRIMARY_ACTION = { text: TEXTS.DELETE_CARD_BUTTON_PRIMARY_CTA, onClick: props.onDeleteCardClick };
    const SECONDARY_ACTION = { text: TEXTS.DELETE_CARD_BUTTON_SECONDARY_CTA, onClick: props.onClose };

    useEffectOnce(() => {
        deleteHalfCardScreenViewEvent(screenViewEvent);
    });

    return (
        <DeleteCardPromptUI
            title={TEXTS.DELETE_CARD_TITLE}
            message={TEXTS.DELETE_CARD_MESSAGE}
            primaryAction={PRIMARY_ACTION}
            secondaryAction={SECONDARY_ACTION}
            hideOnOverlayClick={false}
            onClose={props.onClose}
        />
    );
});

export const DeleteCardAlert = memo(function DeleteCard(props: DeleteCardProps): ReactElement | null {
    const { cardDetails, resetDeleteCardAlert } = props;

    const { clickEvent } = React.useContext(AnalyticsContext);
    const apiContextValue = useContext(ApiContext);
    const apiCallRef = useRef(false);
    const apiHost = apiContextValue.host.unwrap();

    const [showDeleteCardAlertPrompt, setshowDeleteCardAlertPrompt] = useState<boolean>(true);
    const [showDeleteCardConfirmPrompt, setShowDeleteCardConfirmPrompt] = useState<boolean>(false);

    const onPromptClose = useCallback((): void => {
        //Analytics
        onDeleteCardCancelEvent(clickEvent);

        setshowDeleteCardAlertPrompt(false);
        setShowDeleteCardConfirmPrompt(false);
        resetDeleteCardAlert();
    }, [resetDeleteCardAlert, clickEvent]);

    const onDeleteConfirmPromptClose = useCallback(() => {
        //Analytics
        onDeleteCardDismissEvent(clickEvent);

        setShowDeleteCardConfirmPrompt(false);
        resetDeleteCardAlert();
        closeWebview();
    }, [resetDeleteCardAlert, clickEvent]);

    const deleteCardAction = useCallback(async () => {
        const deleteCardResponse = await deleteCardPublic(apiHost, cardDetails.cardReference);

        if (deleteCardResponse.statusCode !== API_SUCCESS_STATUS_CODE) {
            throw deleteCardResponse;
        }
        apiCallRef.current = false;
        setShowDeleteCardConfirmPrompt(true);
    }, [apiHost, cardDetails.cardReference]);

    const onDeleteCardClick = useCallback(async () => {
        if (apiCallRef.current) {
            return;
        }
        setshowDeleteCardAlertPrompt(false);
        apiCallRef.current = true;

        try {
            await deleteCardAction();
        } catch (error) {
            apiCallRef.current = false;
            // (5.8) Card deletion failure — UI shows an error but previously emitted no NR event.
            recordNREvent({
                eventType: NEWRELIC_TABLES.WP_JUSPAY_CARD_FLOW_ERROR,
                state: WP_JUSPAY_CARD_FLOW_ERROR_STATE.DELETE_CARD_ERROR,
                cardReference: String(Boolean(cardDetails.cardReference)),
                errorMessage:
                    (error as { statusMessage?: string })?.statusMessage ??
                    (error as Error)?.message ??
                    String(error ?? "unknown"),
                errorStatus: (error as { statusCode?: number })?.statusCode ?? "--",
            });
            resetDeleteCardAlert();
            showNativeAlert({
                message: `${OOPS_SOMETHING_WENT_WRONG}. Please try again`,
            });
        }
    }, [deleteCardAction, resetDeleteCardAlert, cardDetails.cardReference]);

    if (showDeleteCardAlertPrompt) {
        return <DeleteCardPrompt onClose={onPromptClose} onDeleteCardClick={onDeleteCardClick} />;
    }

    if (showDeleteCardConfirmPrompt) {
        return <DeleteCardConfirmPrompt cardBrand={cardDetails.cardBrand} onClose={onDeleteConfirmPromptClose} />;
    }

    if (apiCallRef.current === true) {
        return <Loader />;
    }

    return null;
});
